/**
 * View-model types for the revamped /admin/pods dashboard.
 *
 * Counts + per-org coverage are live (see podDashboard.api.ts, which reuses the
 * existing pod-count backend plus the new admin/pods/org-coverage endpoint).
 * Revenue figures have no backend source yet and render as "—".
 */

/**
 * Flat active-monthly-revenue estimate per paid pod subscription (USD).
 * Applied to subscribed (paying) pods — org paid pods + active athlete subs.
 * Swap for a real per-subscription figure once a revenue source is wired.
 */
export const PAID_POD_MONTHLY_RATE = 60;

/** The five top-level buckets shown in the overview bar and status rows. */
export type PodStatusKey = 'subscribed' | 'past_due' | 'comp_free' | 'in_stock' | 'decommissioned';

/** Status colors tuned to match the approved mockup. */
export const POD_STATUS_COLORS: Record<PodStatusKey, string> = {
    subscribed: '#3F9C53', // green — paying
    past_due: '#C8901C', // amber — lapsed, actionable
    comp_free: '#7E57C2', // purple — comped / free
    in_stock: '#2E6FD6', // blue — inventory
    decommissioned: '#8A94A6', // gray — retired
};

/** Human-readable label + one-line description for each status row. */
export const POD_STATUS_META: Record<PodStatusKey, { label: string; description: string }> = {
    subscribed: {
        label: 'Subscribed',
        description: 'Paying pods generating revenue — by organization and athlete.',
    },
    past_due: {
        label: 'Past due / inactive',
        description: 'Lapsed or canceled subscriptions with pods still out — collect or recover.',
    },
    comp_free: {
        label: 'Comped / Free',
        description: 'Deployed but not paying — marked comped, or never billed.',
    },
    in_stock: {
        label: 'In stock',
        description: 'Owned but not deployed — available to ship or assign.',
    },
    decommissioned: {
        label: 'Decommissioned',
        description: 'Permanently removed from the fleet.',
    },
};

/** Display order of the status rows, top to bottom. */
export const POD_STATUS_ORDER: PodStatusKey[] = [
    'subscribed',
    'past_due',
    'comp_free',
    'in_stock',
    'decommissioned',
];

/** One status bucket: a count plus an optional money figure shown on the row. */
export interface PodStatusSegment {
    key: PodStatusKey;
    podCount: number;
    /** Monthly recurring revenue (USD). null until a revenue source is wired. */
    monthlyRevenue?: number | null;
}

/** One organization row in the Subscribed → Org drill-down (paid pods only). */
export interface OrgSubscribedRow {
    holder: string;
    /** Stripe customer id (cus_…) for an "open in Stripe" link; null if none. */
    stripeCustomerId: string | null;
    /** Total paid pods (Stripe-subscribed + externally invoiced). */
    pods: number;
    /** Subset of `pods` billed via external invoice (excluded from revenue). */
    externalInvoicePods: number;
}

/** One athlete row in the Subscribed → Athlete drill-down. */
export interface AthleteSubscribedRow {
    /** Linked individual UUID; stable key for the row. May be null if unlinked. */
    individualUuid: string | null;
    name: string;
    /** Athlete's Stripe subscription id (sub_…) for an "open in Stripe" link; null if none. */
    stripeSubscriptionId: string | null;
    pods: number;
}

/** One organization group of subscribed athletes, sorted by pod count. */
export interface AthleteSubscribedOrgGroup {
    holder: string;
    pods: number;
    athletes: AthleteSubscribedRow[];
}

/** Org vs. athlete breakdown for the Subscribed bucket. */
export interface SubscribedBreakdown {
    org: {
        pods: number;
        /** Subset of org `pods` billed via external invoice (excluded from revenue). */
        externalInvoicePods: number;
        holders: OrgSubscribedRow[];
    };
    athlete: {
        /** Active athlete-subscription pods. */
        pods: number;
        /** Subscribed athletes grouped by organization (sorted by pod count). */
        orgs: AthleteSubscribedOrgGroup[];
    };
    /**
     * Counted (subscribed) pods billing $0 via a 100%-off Stripe coupon or standing
     * credit. Excluded from the list-price revenue estimate; surfaced as a note.
     */
    zeroChargePods: number;
}

/** One organization row in the Comped/Free drill-down. */
export interface OrgCompFreeRow {
    holder: string;
    /** Stripe customer id (cus_…) for an "open in Stripe" link; null if none. */
    stripeCustomerId: string | null;
    comped: number;
    free: number;
}

/** Marked-comped vs. unmarked-free breakdown for the Comped/Free bucket. */
export interface CompFreeBreakdown {
    /** Pods flagged is_comp. */
    marked: number;
    /** Unpaid pods with no comp flag in orgs that were never billed (overflow + never-subscribed). */
    unmarked: number;
    /** Per-org comped/free rows (orgs with any comped or free pods). */
    orgs: OrgCompFreeRow[];
}

/** One organization row in the Past due / inactive drill-down. */
export interface OrgPastDueRow {
    holder: string;
    /** Stripe customer id (cus_…) for an "open in Stripe" link; null if none. */
    stripeCustomerId: string | null;
    pods: number;
}

/**
 * Past due / inactive breakdown: pods whose subscription lapsed (canceled /
 * past-due) and are still out — actionable for collection or recovery.
 */
export interface PastDueBreakdown {
    /** Org pods in orgs with a lapsed (non-active) subscription on record. */
    orgPods: number;
    /** Athlete pods whose subscription is no longer active. */
    athletePods: number;
    /** Per-org rows (orgs with lapsed-subscription pods). */
    orgs: OrgPastDueRow[];
    /** Past-due athletes grouped by organization (sorted by pod count). */
    athleteOrgs: AthleteSubscribedOrgGroup[];
}

/** Flags marking which parts of the data are still placeholder. */
export interface PodDashboardPlaceholders {
    /** Revenue figures (no backend source yet). */
    revenue: boolean;
}

export interface PodDashboardData {
    /** Total pods purchased / in existence. */
    totalPods: number;
    /**
     * Headline active monthly recurring revenue (USD), list-price estimate.
     * Excludes externally-invoiced and Stripe zero-charge (comped/credit) pods.
     */
    activeMonthlyRevenue: number | null;
    /**
     * Actual recurring monthly revenue (USD) from Stripe — honors coupons, combo
     * pricing, and credit. Shown beside the estimate. null until the pull succeeds.
     */
    actualMonthlyRevenue: number | null;
    /** Counted pods billing $0 via a Stripe coupon/credit (shown as a callout). */
    subscribedZeroChargePods: number;
    /** Percent of total pods that are deployed (subscribed). */
    deployedPercent: number;
    /** The five status buckets, in display order. */
    segments: PodStatusSegment[];
    subscribed: SubscribedBreakdown;
    pastDue: PastDueBreakdown;
    compFree: CompFreeBreakdown;
    placeholders: PodDashboardPlaceholders;
}
